import { Price, ProductDuration } from "./support"

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

const SUPPORTED_UTM_KEYS = [
  "utm_medium",
  "utm_source",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
] as const

const pushedEvents = new Set<string>()

function dataLayer(): Array<Record<string, unknown>> {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function utmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)

  return SUPPORTED_UTM_KEYS.reduce<Record<string, string>>((memo, key) => {
    const value = params.get(key)
    if (!value) return memo

    return {
      ...memo,
      [key]: value,
    }
  }, {})
}

function amountFromMinorUnits(amount: number): number {
  return amount / 100
}

function buildItems(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
): Array<Record<string, unknown>> {
  return [
    {
      item_id: price.price_id,
      item_name: "Surf-Forecast Membership",
      item_category: "subscription",
      item_variant: subscription,
      price: amountFromMinorUnits(amountMinorUnits),
      quantity: 1,
    },
  ]
}

function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  dataLayer().push({
    event,
    ...utmParams(),
    ...params,
  })
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>,
  params: Record<string, unknown> = {},
): void {
  dataLayer().push({ ecommerce: null })
  pushEvent(event, {
    ...params,
    ecommerce,
  })
}

function pushOnce(key: string, callback: () => void): void {
  if (pushedEvents.has(key)) return

  pushedEvents.add(key)
  callback()
}

export function trackSignUp(method = "email"): void {
  pushEvent("sign_up", { method })
}

export function trackLogin(method = "email"): void {
  pushEvent("login", { method })
}

export function trackBeginCheckout(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  coupon?: string,
): void {
  pushOnce(`begin_checkout:${price.price_id}:${amountMinorUnits}:${coupon || ""}`, () => {
    pushEcommerceEvent("begin_checkout", {
      currency: price.currency.toUpperCase(),
      value: amountFromMinorUnits(amountMinorUnits),
      coupon,
      items: buildItems(subscription, price, amountMinorUnits),
    })
  })
}

export function trackAddPaymentInfo(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  paymentType: string,
  coupon?: string,
): void {
  pushEcommerceEvent("add_payment_info", {
    currency: price.currency.toUpperCase(),
    value: amountFromMinorUnits(amountMinorUnits),
    coupon,
    payment_type: paymentType,
    items: buildItems(subscription, price, amountMinorUnits),
  })
}

export function trackPurchase(
  transactionId: string,
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  coupon?: string,
): void {
  pushOnce(`purchase:${transactionId}`, () => {
    pushEcommerceEvent("purchase", {
      transaction_id: transactionId,
      currency: price.currency.toUpperCase(),
      value: amountFromMinorUnits(amountMinorUnits),
      coupon,
      items: buildItems(subscription, price, amountMinorUnits),
    })
  })
}

export function trackPromoCodeOpen(): void {
  pushEvent("promo_code_open")
}

export function trackPromoCodeApplied(params: Record<string, unknown> = {}): void {
  pushEvent("promo_code_applied", params)
}
